import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { SelectButton } from 'primereact/selectbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationService } from '../../services/notificationService';
import { ActivityService } from '../../services/activityService';
import { Notification, NotificationFilter } from '../../types/notification';
import './NotificationsPage.css';

export const NotificationsPage: React.FC = () => {
	const { currentAgent } = useAuth();
	const navigate = useNavigate();
	const toast = useRef<Toast>(null);

	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedNotifications, setSelectedNotifications] = useState<Notification[]>([]);
	const [filter, setFilter] = useState<NotificationFilter>({});
	const [viewFilter, setViewFilter] = useState<string>('all');

	const viewOptions = [
		{ label: 'All', value: 'all' },
		{ label: 'Unread', value: 'unread' },
		{ label: 'Read', value: 'read' }
	];

	const typeOptions = [
		{ label: 'All Types', value: 'all' },
		{ label: 'Info', value: 'info' },
		{ label: 'Success', value: 'success' },
		{ label: 'Warning', value: 'warning' },
		{ label: 'Error', value: 'error' },
		{ label: 'Agent Insights', value: 'agent_insight' },
		{ label: 'Customer Updates', value: 'customer_update' },
		{ label: 'System', value: 'system' }
	];

	const [typeFilter, setTypeFilter] = useState<string>('all');

	// Load notifications from the realtime subscription (its first emission is the initial load)
	useEffect(() => {
		if (!currentAgent?.id) {
			setNotifications([]);
			setLoading(false);
			return;
		}

		setLoading(true);

		const unsubscribe = NotificationService.subscribeToNotifications(
			currentAgent.id,
			(updatedNotifications) => {
				setNotifications(updatedNotifications);
				setLoading(false);
			},
			filter
		);

		return () => unsubscribe();
	}, [currentAgent?.id, filter]);

	// Handle view filter change
	const handleViewFilterChange = (value: string) => {
		setViewFilter(value);
		if (value === 'all') {
			setFilter({ ...filter, read: undefined });
		} else {
			setFilter({ ...filter, read: value === 'read' });
		}
	};

	// Handle type filter change
	const handleTypeFilterChange = (value: string) => {
		setTypeFilter(value);
		if (value === 'all') {
			setFilter({ ...filter, type: undefined });
		} else {
			setFilter({ ...filter, type: value });
		}
	};

	// Mark as read
	const markAsRead = async (notification: Notification) => {
		if (notification.read) return;

		try {
			await NotificationService.markAsRead(notification.id);

			// Log activity
			if (currentAgent?.id) {
				await ActivityService.logActivity(
					currentAgent.id,
					'notification_viewed',
					'notification',
					notification.id,
					notification.title,
					{
						notificationTitle: notification.title
					}
				);
			}

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Notification marked as read'
			});
		} catch (error) {

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to mark as read'
			});
		}
	};

	// Mark selected as read
	const markSelectedAsRead = async () => {
		const unreadIds = selectedNotifications
			.filter(n => !n.read)
			.map(n => n.id);

		if (unreadIds.length === 0) {
			toast.current?.show({
				severity: 'info',
				summary: 'Info',
				detail: 'No unread notifications selected'
			});
			return;
		}

		try {
			await NotificationService.markMultipleAsRead(unreadIds);
			setSelectedNotifications([]);
			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: `Marked ${unreadIds.length} notifications as read`
			});
		} catch (error) {

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to mark notifications as read'
			});
		}
	};

	// Delete notification
	const deleteNotification = async (notification: Notification) => {
		confirmDialog({
			message: 'Are you sure you want to delete this notification?',
			header: 'Delete Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: async () => {
				try {
					await NotificationService.deleteNotification(notification.id);
					toast.current?.show({
						severity: 'success',
						summary: 'Success',
						detail: 'Notification deleted'
					});
				} catch (error) {

					toast.current?.show({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to delete notification'
					});
				}
			}
		});
	};

	// Handle notification click
	const handleNotificationClick = async (notification: Notification) => {
		// Mark as read
		await markAsRead(notification);

		// Navigate if there's an action URL
		if (notification.metadata?.actionUrl) {
			navigate(notification.metadata.actionUrl);
		}
	};

	// Type badge template
	const typeBodyTemplate = (notification: Notification) => {
		const severityMap: Record<string, 'info' | 'success' | 'warning' | 'danger'> = {
			'info': 'info',
			'success': 'success',
			'warning': 'warning',
			'error': 'danger',
			'agent_insight': 'info',
			'customer_update': 'warning',
			'system': 'danger'
		};

		const labelMap: { [key: string]: string } = {
			'info': 'Info',
			'success': 'Success',
			'warning': 'Warning',
			'error': 'Error',
			'agent_insight': 'Insight',
			'customer_update': 'Customer',
			'system': 'System'
		};

		return (
			<Badge
				value={labelMap[notification.type] || notification.type}
				severity={severityMap[notification.type]}
			/>
		);
	};

	// Title template
	const titleBodyTemplate = (notification: Notification) => {
		return (
			<div
				className={`notification-title ${!notification.read ? 'unread' : ''}`}
				onClick={() => handleNotificationClick(notification)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						if (e.key === ' ') e.preventDefault();
						handleNotificationClick(notification);
					}
				}}
				style={{ cursor: 'pointer' }}
			>
				{!notification.read && <i className="pi pi-circle-fill unread-indicator"></i>}
				{notification.title}
			</div>
		);
	};

	// Date template
	const dateBodyTemplate = (notification: Notification) => {
		return new Date(notification.createdAt).toLocaleString();
	};

	// Actions template
	const actionsBodyTemplate = (notification: Notification) => {
		return (
			<div className="notification-actions">
				{!notification.read && (
					<Button
						icon="pi pi-check"
						className="p-button-rounded p-button-text p-button-sm"
						tooltip="Mark as read"
						onClick={() => markAsRead(notification)}
					/>
				)}
				<Button
					icon="pi pi-trash"
					className="p-button-rounded p-button-text p-button-danger p-button-sm"
					tooltip="Delete"
					onClick={() => deleteNotification(notification)}
				/>
			</div>
		);
	};

	const header = (
		<div className="notifications-header">
			<div className="header-left">
				<h2>Notifications</h2>
				<Badge
					value={notifications.filter(n => !n.read).length}
					severity="danger"
					className="unread-count"
				/>
			</div>
			<div className="header-actions">
				{selectedNotifications.length > 0 && (
					<Button
						label="Mark Selected as Read"
						icon="pi pi-check"
						className="p-button-secondary"
						onClick={markSelectedAsRead}
					/>
				)}
			</div>
		</div>
	);

	return (
		<div className="notifications-page">
			<Toast ref={toast} />
			<ConfirmDialog />

			<Card className="notifications-card">
				<div className="filters-section">
					<SelectButton
						value={viewFilter}
						options={viewOptions}
						onChange={(e) => handleViewFilterChange(e.value)}
						className="view-filter"
					/>
					<SelectButton
						value={typeFilter}
						options={typeOptions}
						onChange={(e) => handleTypeFilterChange(e.value)}
						className="type-filter"
					/>
				</div>

				<DataTable
					value={notifications}
					selection={selectedNotifications}
					onSelectionChange={(e) => setSelectedNotifications(e.value as Notification[])}
					selectionMode="multiple"
					dataKey="id"
					paginator
					rows={10}
					rowsPerPageOptions={[10, 25, 50]}
					loading={loading}
					header={header}
					emptyMessage="No notifications found"
					className="notifications-table"
				>
					<Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
					<Column field="type" header="Type" body={typeBodyTemplate} style={{ width: '120px' }} />
					<Column field="title" header="Title" body={titleBodyTemplate} />
					<Column field="message" header="Message" style={{ width: '40%' }} />
					<Column field="createdAt" header="Date" body={dateBodyTemplate} style={{ width: '180px' }} />
					<Column body={actionsBodyTemplate} style={{ width: '100px' }} />
				</DataTable>
			</Card>
		</div>
	);
};